import React, { useEffect, useRef, useState } from 'react';
import DiscountBadge from "../Common/DiscountBadge";
import { Link, useNavigate } from "react-router-dom";
import ColorSwatch from "../Common/ColorSwatch";
import ProductListPrice from "../Common/ProductListPrice";
import { useSelector } from "react-redux";
import placeholderImage from "../../assets/placeholder.jpg";
import WishlistHeartIcon from "../Common/WishlistHeartIcon";
import useActivePromotionsMap, { getPromotionBadgeForProduct } from "../../hooks/useActivePromotionsMap";

const toQuantityNumber = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
        const parsed = Number(value.trim().replace(",", "."));
        return Number.isFinite(parsed) ? parsed : 0;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

// Helper function to check if all SKUs have quantity 0
const isOutOfStock = (product) => {
    if (!product?.variants || product.variants.length === 0) {
        return false; // If no variants, assume in stock
    }

    let totalSkus = 0;
    let skusWithStock = 0;

    for (const variant of product.variants) {
        if (!variant?.sizes || variant.sizes.length === 0) continue;
        
        for (const size of variant.sizes) {
            if (!size?.skus || size.skus.length === 0) continue;
            
            for (const sku of size.skus) {
                totalSkus++;
                
                // Check quantity directly or in stocks array
                const quantity = toQuantityNumber(sku?.quantity);
                const stocksQuantity = sku?.stocks?.reduce((sum, stock) => sum + toQuantityNumber(stock?.quantity), 0) ?? 0;
                const totalQuantity = quantity + stocksQuantity;
                
                if (totalQuantity > 0) {
                    skusWithStock++;
                }
            }
        }
    }

    // If there are SKUs but none have stock, product is out of stock
    return totalSkus > 0 && skusWithStock === 0;
};

const plpSessionKeys = (scope) =>
    scope
        ? { scrollY: `plp_scrollY_${scope}`, productId: `plp_productId_${scope}` }
        : { scrollY: "plp_scrollY", productId: "plp_productId" };

const ProductItem = (props) => {
    const { item, hideFavorite, filters, plpStorageScope } = props;
    const { promotionsByLocation } = useActivePromotionsMap();
    const navigate = useNavigate();
    const firstVariant = item?.variants?.[0];
    const filterData = props.products;
    const wishlistItems = useSelector(state => state?.cart?.wishlistItems);
    const ServicesRef = useRef(null);
    const localId = localStorage.getItem("mylocalid");
    const itemProps = item?._id === localId ? { ref: ServicesRef } : {};
    const isInWishlist = wishlistItems?.some((e) => e?._id === item?._id);
    const [imageLoaded, setImageLoaded] = useState(false);

    // Handle navigation to product details with filters in state
    const handleProductClick = (e) => {
        localStorage.setItem("currUrl", window.location.href);
        const productKey = item?._id ?? item?.externalId ?? item?.id;
        const keys = plpSessionKeys(plpStorageScope);
        try {
            sessionStorage.setItem(keys.scrollY, String(window.scrollY || 0));
            if (productKey) sessionStorage.setItem(keys.productId, String(productKey));
        } catch {}
        sessionStorage.setItem("clickedProductId", item?._id);
        
        // Store filters in sessionStorage for use in ProductDetails
        if (filters) {
            sessionStorage.setItem("productFilters", JSON.stringify(filters));
        }
        
        if (props.onProductClick) props.onProductClick();
    };

    const variantData = item?.variants?.[0];
    const isNew = !!variantData?.sizes?.find(s => s?.skus?.find(sk => sk.NewIn > 0))
    const priceDetails = variantData?.sizes?.[0]?.skus[0] || {};
    const firstImage = process.env.REACT_APP_IMAGE_URL + firstVariant?.newImagesUrl?.[0];
    const ImageUrl = process.env.REACT_APP_IMAGE_URL + variantData?.newImagesUrl?.[0];
    
    const outOfStock = isOutOfStock(item);
    const promotionLabel = getPromotionBadgeForProduct(promotionsByLocation, item?.location);

    // Reset shimmer when card changes (e.g. Swiper reuses slides)
    useEffect(() => {
        setImageLoaded(false);
    }, [ImageUrl, item?._id]);

    return (
        <div className="w-full pb-4 h-full" {...itemProps} >
            <div className="ProductItem ProductItem--big fade-in-down min-h-[300px] h-full rounded-2xl bg-white ring-1 ring-black/5 shadow-[0_18px_55px_-45px_rgba(0,0,0,0.55)] hover:shadow-[0_30px_80px_-55px_rgba(0,0,0,0.7)] transition-shadow flex flex-col">
                <div className="ProductItem__image !border-0 rounded-2xl overflow-hidden">
                    <Link
                        to={`/productdetails/${item?.externalId}/${item?.categories?.[0] ?? ''}`}
                        state={{ filters: filters }}
                        onClick={handleProductClick}
                    >
                        <div className="bg-white flex items-center justify-center">
                            <div className="image-wrapper relative">
                                <img
                                    src={ImageUrl || placeholderImage}
                                    alt={item?.description || "Product Image"}
                                    loading="lazy"
                                    decoding="async"
                                    fetchPriority="low"
                                    draggable={false}
                                    onLoad={() => setImageLoaded(true)}
                                    onError={() => setImageLoaded(true)}
                                    className="w-full h-[160px] sm:h-[200px] md:h-[260px] object-cover transition-transform duration-500 ease-out hover:scale-[1.04]"
                                />
                                {!imageLoaded && <span className="skeleton" />}
                            </div>

                        </div>
                    </Link>
                    {isNew && (
                        <div className="absolute top-3 left-3 z-10">
                            {/* "Pinned" premium tag */}
                            <span
                                className="absolute top-1/2 -translate-y-1/2 left-2 h-3 w-3 rounded-full border border-white/70 bg-white z-20"
                                style={{
                                    boxShadow:
                                        '0 1px 0 rgba(255,255,255,0.55), 0 10px 18px -14px rgba(0,0,0,0.8), inset 0 -2px 4px rgba(0,0,0,0.22), inset 0 1px 2px rgba(255,255,255,0.65)'
                                }}
                                aria-hidden="true"
                            />
                            <span
                                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 pl-3.5 text-[10px] font-semibold tracking-[0.26em] uppercase shadow-[0_18px_50px_-28px_rgba(0,0,0,0.65)] backdrop-blur-md"
                                style={{
                                    borderColor: 'rgba(199,164,106,0.35)',
                                    background:
                                        'linear-gradient(135deg, rgba(18,18,18,0.92), rgba(0,0,0,0.78))',
                                    color: 'rgba(255,255,255,0.92)'
                                }}
                            >
                                <span
                                    className="h-1.5 w-1.5 rounded-full"
                                    style={{ backgroundColor: 'var(--albi-accent)' }}
                                    aria-hidden="true"
                                />
                                NEW
                            </span>
                        </div>
                    )}
                    <DiscountBadge product={priceDetails} />

                    {promotionLabel && (
                        <span className="promotion-bar" title={promotionLabel}>
                            {promotionLabel}
                        </span>
                    )}

                    {/* Out of Stock Badge */}
                    {outOfStock && (
                        <span className='out-of-stock-bar'>
                            Out of Stock
                        </span>
                    )}
                </div>

                {/* Product Content */}
                <div className="ProductItem__content px-3 pt-3">
                    <span className="ProductItem__brand-name prod-collection text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-600">
                        {item?.brand}
                    </span>
                </div>

                <div>
                    <div className="prod-collection flex items-start justify-between px-3 gap-3">
                        <Link 
                            to={`/productdetails/${item?.externalId}/${item?.categories?.[0] ?? ''}`}
                            state={{ filters: filters }}
                            className="flex-1 min-w-0"
                            onClick={handleProductClick}
                        >
                            <h4 className="ProductItem__content__title text-[13px] sm:text-[14px] font-medium leading-snug text-neutral-900 line-clamp-2 min-h-[2.6em] sm:min-h-[2.8em]">
                                {item?.name}
                            </h4>
                        </Link>

                        {/* Wishlist Button (heart icon, same behavior as ProductDetails).
                            addItemToWishlist është opsional – nëse s'vjen si prop, mos bëj asgjë në klik. */}
                        {!hideFavorite && (
                            <button
                                type="button"
                                aria-label="Toggle wishlist"
                                className="mt-0.5 h-9 w-9 rounded-full grid place-items-center bg-white/90 ring-1 ring-black/10 hover:ring-black/20 shadow-sm transition"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (typeof props.addItemToWishlist === 'function') {
                                        props.addItemToWishlist(item);
                                    }
                                }}
                            >
                                <WishlistHeartIcon size={20} className="icon icon-tabler icon-tabler-heart" />
                            </button>
                        )}
                    </div>

                    {/*
                                        <div className="ProductPrices sale prod-collection">
                                            {salePrice > 0 ? (
                                                <>
                                                    <h4 className="regular">
                                                        <del>{regularPrice?.toFixed(2)}€</del>
                                                    </h4>
                                                    <h4 className="regular salening">{salePrice?.toFixed(2)}€</h4>
                                                </>
                                            ) : (
                                                regularPrice > 0 && <h4 className="regular">{regularPrice?.toFixed(2)}€</h4>
                                            )}
                                        </div> */}

                    <div className="px-3 pb-3 mt-auto">
                        <ProductListPrice product={priceDetails} />
                        <ColorSwatch variants={item?.variants} />
                    </div>
                </div>
            </div>
        </div>
    )
}
export default ProductItem